import StaffNotesPanel from './StaffNotesPanel';
import React, { useEffect, useState } from 'react';
import EditStaffDialog from './EditStaffDialog';
import { isInternalStaff } from '../utils/staff.helpers';
import { formatCurrency } from '../../dashboard/utils/dashboard.helpers';
import '../styles/staff-detail.css';
import { useNavigate } from 'react-router-dom';
import EntityDetailDialog from '../../../shared/components/EntityDetailDialog';
import IconAction from '../../../shared/components/IconAction';
import { useOptionalSession } from '../../../app/providers/session-context';
import StaffSkillsSection from '@/modules/staff/components/StaffSkillsSection';
import { useStaffOperationalDetail } from '../hooks/useStaffOperationalDetail';
import ClientCustomFieldsSection from '../../clients/components/ClientCustomFieldsSection';
import {
  archiveStaffCustomField,
  createStaffCustomField,
  listStaffCustomFields,
} from '../services/staff.service';
import { getStaffInitials, getStaffTypeMeta } from '@/modules/staff/utils';

function valueOrDash(value) {
  return value || '—';
}

function getOperationalValue(status, value) {
  if (status === 'loading') return '…';
  if (!['ready', 'empty'].includes(status)) return '—';
  return value ?? '—';
}

function getAccessLabel(staffMember = {}) {
  if (!staffMember.linkedWorkspaceUserId) return 'Senza accesso gestionale';
  if (staffMember.accountActive === false || staffMember.workspaceUserActive === false)
    return 'Account disattivato';
  return 'Con accesso al gestionale';
}

const STAFF_CUSTOM_FIELDS_API = {
  list: listStaffCustomFields,
  create: createStaffCustomField,
  archive: archiveStaffCustomField,
};

function DetailSection({ title, children }) {
  return <section className="staff-detail__section" aria-label={title}>
    <h3 className="staff-detail__section-title">{title}</h3>
    {children}
  </section>;
}

function DetailField({ label, value }) {
  return <div className="staff-detail__field">
    <dt>{label}</dt>
    <dd>{valueOrDash(value)}</dd>
  </div>;
}

function OverviewMetric({ label, value, description, onClick }) {
  const Element = onClick ? 'button' : 'div';
  return <Element className="staff-detail__metric" {...(onClick ? { type: 'button', onClick } : {})}>
    <span className="staff-detail__metric-label">{label}</span>
    <strong className="staff-detail__metric-value">{value}</strong>
    <span className="staff-detail__metric-description">{description}</span>
  </Element>;
}

export default function StaffDetailDialog({
  open,
  onOpenChange,
  staffMember,
  onSave,
  canManageStaff = false,
  onNavigateAway,
}) {
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(false);
  }, [staffMember?.id, open]);
  const navigate = useNavigate();
  const { currentUser } = useOptionalSession();
  const operational = useStaffOperationalDetail(staffMember, open);

  if (!staffMember) return null;

  const resolvedStaffMember = operational.enrichedStaffMember || staffMember;
  const type = getStaffTypeMeta(resolvedStaffMember.tipoPersonale);
  const accessLabel = getAccessLabel(resolvedStaffMember);
  const address =
    resolvedStaffMember.address ||
    resolvedStaffMember.indirizzo ||
    resolvedStaffMember.registeredAddress ||
    resolvedStaffMember.sede ||
    '';
  const iban = resolvedStaffMember.iban || resolvedStaffMember.IBAN || '';

  const tasksReady = ['ready', 'empty'].includes(operational.status);
  const dealsReady = operational.dealsStatus === 'ready';
  const urgentTasks = operational.activeAssignments.filter(task => task.urgency === 'alta').length;
  const isOwnProfile = Boolean(currentUser?.id && resolvedStaffMember.linkedWorkspaceUserId
    && String(currentUser.id) === String(resolvedStaffMember.linkedWorkspaceUserId));

  function openProfileSettings() {
    onNavigateAway?.();
    onOpenChange?.(false);
    navigate('/impostazioni');
  }

  function openAssignmentsTab(tab) {
    onNavigateAway?.();
    const params = new URLSearchParams({ assignmentTab: tab });
    if (resolvedStaffMember.id) {
      params.set('staffId', String(resolvedStaffMember.id));
    }
    navigate(`/incarichi?${params.toString()}`);
  }

  if (editing && !isInternalStaff(staffMember)) {
    return <EditStaffDialog member={staffMember} onSave={onSave} onClose={() => setEditing(false)} />;
  }

  return (
    <EntityDetailDialog
      isOpen={open}
      onClose={() => onOpenChange?.(false)}
      title={resolvedStaffMember.name}
      avatarLabel={getStaffInitials(resolvedStaffMember.name)}
      badges={resolvedStaffMember.role ? [{ label: resolvedStaffMember.role, tone: 'active' }] : []}
      editActions={
        canManageStaff && !isInternalStaff(staffMember) && onSave ? (
          <IconAction iconKey="actions.edit" label="Modifica personale" onClick={() => setEditing(true)} />
        ) : isOwnProfile ? (
          <IconAction iconKey="actions.edit" label="Modifica profilo" onClick={openProfileSettings} />
        ) : null
      }
      className="staff-detail-dialog"
    >
      <div className="staff-detail__layout">
        <DetailSection title="Informazioni aziendali">
          <dl className="staff-detail__field-grid">
            <DetailField label="Ruolo" value={resolvedStaffMember.role} />
            <DetailField label="Tipo di personale" value={type.label} />
            <DetailField label="Accesso" value={accessLabel} />
            <DetailField label="P.IVA" value={resolvedStaffMember.vatNumber || resolvedStaffMember.partitaIva} />
            <DetailField label="Codice fiscale" value={resolvedStaffMember.taxCode || resolvedStaffMember.codiceFiscale} />
            <DetailField label="IBAN" value={iban} />
          </dl>
        </DetailSection>

        <DetailSection title="Contatti">
          <div className="staff-detail__contacts">
            <div>
              <h4>Contatti personali</h4>
              <dl className="staff-detail__contact-fields">
                <DetailField label="Email" value={resolvedStaffMember.personalEmail || resolvedStaffMember.email} />
                <DetailField label="Telefono" value={resolvedStaffMember.personalPhone || resolvedStaffMember.phone} />
                <DetailField label="Indirizzo" value={address} />
              </dl>
            </div>
            <div>
              <h4>Contatti aziendali</h4>
              <dl className="staff-detail__contact-fields">
                <DetailField label="Email" value={resolvedStaffMember.companyEmail} />
                <DetailField label="Telefono" value={resolvedStaffMember.companyPhone} />
              </dl>
            </div>
          </div>
        </DetailSection>

        <DetailSection title="Competenze">
          <StaffSkillsSection skills={resolvedStaffMember.skills} compact />
        </DetailSection>

        <DetailSection title="Overview personale">
          <div className="staff-detail__metrics">
            <OverviewMetric label="Task in corso"
              value={getOperationalValue(operational.status, operational.activeAssignments.length)}
              description={tasksReady
                ? `${urgentTasks} ${urgentTasks === 1 ? 'urgente' : 'urgenti'} · ${operational.activeAssignments.length - urgentTasks} normali`
                : operational.status === 'loading' ? 'Caricamento…' : 'Dati non disponibili'}
              onClick={() => openAssignmentsTab('in_corso')} />
            <OverviewMetric label="Affari aperti"
              value={getOperationalValue(operational.dealsStatus, operational.openDealsCount)}
              description={dealsReady
                ? `${operational.proposalsCount} ${operational.proposalsCount === 1 ? 'proposta' : 'proposte'} · ${operational.inProgressDealsCount} in corso`
                : operational.dealsStatus === 'loading' ? 'Caricamento…' : 'Dati non disponibili'} />
            <OverviewMetric label="Valore pipeline"
              value={getOperationalValue(operational.dealsStatus, formatCurrency(operational.pipelineValue))}
              description={dealsReady && operational.paymentsStatus === 'ready' && operational.outstandingAmount != null
                ? `${formatCurrency(operational.outstandingAmount)} da incassare` : 'Da incassare: —'} />
            <OverviewMetric label="Eventi imminenti"
              value={getOperationalValue(operational.calendarStatus, operational.upcomingEventsCount)}
              description={operational.calendarStatus === 'unavailable' ? 'Dati non disponibili' : 'Prossimi 7 giorni'} />
          </div>
          <button type="button" className="staff-detail__completed-link" onClick={() => openAssignmentsTab('completati')}>
            Task completate: {getOperationalValue(operational.status, operational.completedAssignments.length)}
          </button>
        </DetailSection>

        <DetailSection title="Descrizione interna">
          <p className="staff-detail__description">{valueOrDash(resolvedStaffMember.notes)}</p>
        </DetailSection>

        <ClientCustomFieldsSection readOnly formKey="staff.member.create"
          values={resolvedStaffMember.customFields || {}} api={STAFF_CUSTOM_FIELDS_API} />
        <StaffNotesPanel staffMember={resolvedStaffMember} />
      </div>
    </EntityDetailDialog>
  );
}
